import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import Page from 'components/layouts/page/Page';
import { RenderingErrorBoundaryChildProps } from 'components/RenderingErrorBoundary';
import AIButton from 'components/shared/ai/AIButton';
import { DownloadAIExportButton } from 'components/shared/ai/DownloadAIExportButton';
import Explainer from 'components/shared/ai/Explainer';
import SourceChip from 'components/shared/ai/SourceChip';
import SummarySection from 'components/shared/ai/summary/SummarySection';
import TimingExplainer from 'components/shared/ai/TimingExplainer';
import Button from 'components/shared/Button';
import MobileOnly from 'components/shared/responsiveness/MobileOnly';
import SummarizationActionsMenu from 'components/summarization/SummarizationActionsMenu';
import { useSummarization } from 'context/summarization';
import useAIJobPolling from 'hooks/useAIJobPolling';
import { simpleDate } from 'lib/date';
import AIJob, { AIJobOptions } from 'models/AIJob';
import Summarization, { SummarizationOptions } from 'models/Summarization';
import { summarizationQueryKeys } from 'services/api/summarizations';
import { queryClient } from 'services/http';
import { useMobileNavigationActions } from 'stores/mobileNavigationStore';
import { colors } from 'styles/theme/colors';
import { BodySmall, H3 } from 'styles/typography';
import AIOlioLoader from 'svg/AIOlioLoader';
import ErrorWarningIcon from 'svg/ErrorWarningIcon';
import HamburgerMenuIcon from 'svg/HamburgerMenuIcon';
import SavedIcon from 'svg/SavedIcon';
import SummaryFailedIcon from 'svg/SummaryFailedIcon';

export default function ViewSummarization({ isErrored = false }: RenderingErrorBoundaryChildProps) {
  const { setNavigationOpen } = useMobileNavigationActions();
  const navigate = useNavigate();

  const { summarization } = useSummarization();

  const summaryJob = summarization?.summaryJob;
  const summary = summarization?.summary;

  const [pollingJobId, setPollingJobId] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (summaryJob?.isProcessing) {
      setPollingJobId(summaryJob.id);
    }
  }, [summaryJob]);

  const updateSummaryJobCache = useCallback(
    (summaryJob: AIJob) => {
      queryClient.setQueryData(
        summarizationQueryKeys.show({ id: summarization?.id ?? '' }),
        (summarization: Summarization) => {
          return new Summarization({
            ...summarization,
            summaryJobForCurrentAttachments: summaryJob,
          } as SummarizationOptions);
        }
      );
    },
    [summarization]
  );

  const updateSummaryJob = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: summarizationQueryKeys.show({ id: summarization?.id ?? '' }) });
  }, [summarization?.id]);

  const { isPolling } = useAIJobPolling({
    aiJobId: pollingJobId,
    onPollingIntervalSuccess: (job) => {
      if (job.status !== summaryJob?.status || job.id !== summaryJob?.id) {
        if (job.isProcessing) {
          updateSummaryJobCache(job);
        } else {
          updateSummaryJob();
        }
      }
    },
    onFinished: (_finishedJob: AIJob) => {
      setPollingJobId(undefined);
    },
  });

  const handleCollapsePromptExplainer = useCallback(() => {
    updateSummaryJobCache(
      new AIJob({
        ...summaryJob,
        hasCollapsedPromptExplainer: true,
      } as AIJobOptions)
    );
  }, [summaryJob, updateSummaryJobCache]);

  if (summaryJob?.isProcessing || isPolling) {
    // TODO: replace this with newer animation in OLIO-966
    return (
      <Container>
        <LoaderContainer>
          <SpinnerContainer>
            <AIOlioLoader height={120} />
            <span style={{ marginTop: -6 }}>Generating summary...</span>
          </SpinnerContainer>
          <TimingExplainer jobType='summary' />
        </LoaderContainer>
      </Container>
    );
  }

  if (isErrored || !summaryJob || summaryJob.isErrored || !summary) {
    const hasMultiplePatientsError = true; // TODO: replace with real check for specific error type

    return (
      <Container>
        <ErrorContainer>
          <SummaryFailedIcon />
          <span>
            <b>Summary failed to process.</b>
            {hasMultiplePatientsError &&
              ' Multiple patients were identified in the source documentation. Documentation must be specific to a single patient.'}
          </span>
          <div style={{ width: 176 }}>
            <AIButton onClick={() => navigate('/summarizations/new')} fullWidth>
              Start New Summary
            </AIButton>
          </div>
        </ErrorContainer>
      </Container>
    );
  }

  const { name, dateOfBirth, gender } = summarization.patient ?? {};

  const erroredAttachments =
    summarization.attachments?.filter((attachment) => !summaryJob?.attachmentIds.includes(attachment.id)) ?? [];

  return (
    <Page>
      <Page.Header>
        <HeaderGroup>
          <HeaderInfoRow>
            <HeaderInfo>
              <FlexGroup style={{ gap: 16 }}>
                <MobileOnly>
                  <Button variant='icon' onClick={() => setNavigationOpen(true)} data-cy='hamburgerIconButton'>
                    <HamburgerMenuIcon />
                  </Button>
                </MobileOnly>
                <H3>{summarization.title}</H3>
                <FlexGroup style={{ gap: 8 }}>
                  <div>
                    <SavedIcon />
                  </div>
                  <BodySmall>
                    Saved to <Link onClick={() => navigate('/summarizations')}>History</Link>
                  </BodySmall>
                </FlexGroup>
              </FlexGroup>
            </HeaderInfo>
            <SummarizationActionsMenu summarization={summarization} />
          </HeaderInfoRow>
          {name && <BodySmall>{name}</BodySmall>}
          {(dateOfBirth || gender) && (
            <ChipContainer>
              {dateOfBirth && <Chip>DOB: {dateOfBirth}</Chip>}
              {gender && <Chip>Gender: {gender}</Chip>}
            </ChipContainer>
          )}
        </HeaderGroup>
      </Page.Header>
      <Container>
        <SummaryContent>
          {erroredAttachments.length > 0 && (
            <ErroredAttachmentsContainer>
              <ErrorWarningIcon />
              <ErroredAttachmentsText>
                <span>The following file(s) failed to process and are not included in the summary:</span>
                <ErroredAttachmentsList>
                  {erroredAttachments.map((attachment) => (
                    <li key={attachment.id}>{attachment.filename}</li>
                  ))}
                </ErroredAttachmentsList>
              </ErroredAttachmentsText>
            </ErroredAttachmentsContainer>
          )}
          <MetadataSpan>
            Summary created by {summaryJob?.createdBy?.fullName || 'Unknown'}
            {summaryJob?.createdByGroup?.name && ` at ${summaryJob.createdByGroup.name}`} on{' '}
            {summaryJob?.createdAt ? simpleDate(summaryJob.createdAt) : 'Unknown date'}
          </MetadataSpan>
          <ActionContainer>
            <DownloadAIExportButton artifactId={summary.id} artifactType='summary' />
          </ActionContainer>
          <Explainer
            title="How Olio's Summary Works"
            aiPrompt={summary.aiPrompt ?? undefined}
            hasBeenCollapsed={summaryJob?.hasCollapsedPromptExplainer ?? undefined}
            onCollapseSuccess={handleCollapsePromptExplainer}
          />
          <SourcesContainer>
            <SourcesTitle>Sources ({summary.data.sources.length})</SourcesTitle>
            <CitationsContainer>
              {summary.data.sources.map((attachment) => (
                <SourceChip key={attachment.attachmentId} attachment={attachment} />
              ))}
            </CitationsContainer>
          </SourcesContainer>
          <ContentContainer>
            {summary.data.sections.map(({ title, attachmentIds, content }, idx) => (
              <SummarySection
                key={`summary-section-${idx}`}
                title={title}
                attachments={attachmentIds.flatMap(
                  (x) => summary.data.sources.find(({ attachmentId }) => attachmentId == x) || []
                )}
                content={content}
              />
            ))}
          </ContentContainer>
        </SummaryContent>
      </Container>
    </Page>
  );
}

const HeaderGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  margin: 24px 40px 24px 40px;
`;

const HeaderInfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FlexGroup = styled.div`
  display: flex;
  align-items: center;
`;

const Link = styled.a`
  color: var(--primary-blue);
  cursor: pointer;
  text-decoration: underline;
`;

const ChipContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 16px;
`;

const Chip = styled.div<{ backgroundColor?: string; color?: string }>`
  background-color: ${colors.black5};
  color: ${colors.black};
  border-radius: 3px;
  padding: 8px;
  font-size: 12px;
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: default;
`;

const Container = styled(Page.Content)`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 32px 40px 32px 40px;
  margin: 0;
  width: 100%;
  height: 100%;
`;

const SummaryContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const LoaderContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
`;

const SpinnerContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  line-height: 12px;
  letter-spacing: 2%;
`;

const MetadataSpan = styled.span`
  font-style: italic;
  font-size: 12px;
  line-height: 12px;
  letter-spacing: 0.23px;
`;

const ActionContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  flex-wrap: wrap;
`;

const SourcesContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: 16px;
`;

const SourcesTitle = styled.h2`
  font-weight: 700;
  font-size: 14px;
  line-height: 12px;
  letter-spacing: 0.2px;
`;

const CitationsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  flex-wrap: wrap;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ErroredAttachmentsContainer = styled.div`
  border: 1px solid ${colors.accentRed25};
  background-color: ${colors.accentRed10};
  border-radius: 4px;
  padding: 16px;
  font-size: 12px;

  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 8px;
`;

const ErroredAttachmentsText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ErroredAttachmentsList = styled.ul`
  margin: 0;
  padding-left: 20px;

  li {
    margin-bottom: 4px;
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-self: center;
  align-items: center;
  justify-content: start;
  gap: 32px;
  max-width: 640px;
  height: 100%;
  margin-top: 60px;

  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  text-align: center;
`;
